import type { ApprovalLayerDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'
import type { CreateApprovalLayerRequest } from '@/Network/Types/Requests/ApprovalFlowRequests'
import type { ApprovalGroup, ApprovalGroupChild } from '@/types/approval-flow.types'
import {
  LayerOperator,
  MANDATORY_VALIDATION_ROLES,
  ROOT_LAYER_REQUIRES_AND,
  ValidationRole
} from '@/types/approval-flow.types'

export function collectRolesFromGroup(group: ApprovalGroup): ValidationRole[] {
  const roles: ValidationRole[] = []
  for (const child of group.children) {
    if (child.type === 'rule' && child.value !== null) {
      roles.push(child.value)
    } else if (child.type === 'group') {
      roles.push(...collectRolesFromGroup(child))
    }
  }
  return roles
}

function flattenGroupToLayers(group: ApprovalGroup, layers: CreateApprovalLayerRequest[]): void {
  const directRoles: { role: ValidationRole; weight: number }[] = []
  let ruleWeight = 1
  for (const child of group.children) {
    if (child.type === 'rule' && child.value !== null) {
      directRoles.push({ role: child.value, weight: ruleWeight++ })
    }
  }

  layers.push({
    order: layers.length,
    operator: group.operator,
    roles: directRoles
  })

  for (const child of group.children) {
    if (child.type === 'group') {
      flattenGroupToLayers(child, layers)
    }
  }
}

export function createEmptyGroup(): ApprovalGroup {
  return {
    type: 'group',
    operator: LayerOperator.AND,
    children: MANDATORY_VALIDATION_ROLES.map((role) => ({ type: 'rule', value: role }))
  }
}

function isMandatoryRuleChild(child: ApprovalGroupChild): boolean {
  return (
    child.type === 'rule' &&
    child.value !== null &&
    MANDATORY_VALIDATION_ROLES.includes(child.value)
  )
}

/**
 * Garante que os papéis obrigatórios abram o nível raiz do fluxo, na ordem de
 * `MANDATORY_VALIDATION_ROLES`, seguidos das demais regras na ordem original.
 * Normalizar a posição (em vez de só inserir o que falta) mantém a mesma leitura
 * — "Validação 1 = Operação, Validação 2 = Análise de Risco" — tanto num fluxo
 * novo quanto num legado, e de quebra remove obrigatório duplicado.
 *
 * Subgrupos não são inspecionados: um papel obrigatório aninhado poderia ser
 * contornado pelo operador do grupo, então a garantia vale só para a raiz.
 *
 * Com `ROOT_LAYER_REQUIRES_AND` ligada, também força `AND` na raiz — os
 * obrigatórios valem como exigência conjunta ("Operação E Análise de Risco"),
 * e sob `OR` bastaria a assinatura de um deles, o que esvaziaria a regra. Um
 * fluxo legado salvo com raiz `OR` é convertido ao carregar. Desligada, o
 * operador recebido é preservado (a garantia dos papéis obrigatórios continua
 * valendo nos dois regimes). O operador dos subgrupos é sempre preservado.
 *
 * Devolve o próprio grupo, sem cópia, quando ele já está nessa forma.
 */
export function ensureMandatoryRoles(group: ApprovalGroup): ApprovalGroup {
  const alreadyNormalized =
    (!ROOT_LAYER_REQUIRES_AND || group.operator === LayerOperator.AND) &&
    MANDATORY_VALIDATION_ROLES.every((role, index) => {
      const child = group.children[index]
      return child?.type === 'rule' && child.value === role
    }) &&
    !group.children.slice(MANDATORY_VALIDATION_ROLES.length).some(isMandatoryRuleChild)

  if (alreadyNormalized) return group

  return {
    ...group,
    operator: ROOT_LAYER_REQUIRES_AND ? LayerOperator.AND : group.operator,
    children: [
      ...MANDATORY_VALIDATION_ROLES.map((role) => ({ type: 'rule' as const, value: role })),
      ...group.children.filter((child) => !isMandatoryRuleChild(child))
    ]
  }
}

/**
 * Só o que `apiLayersToGroup` realmente lê de uma camada. Assinatura estrutural
 * (em vez de `ApprovalLayerDetail`) para aceitar também as camadas do snapshot de
 * configuração de escala, que não carregam `id`. `ApprovalLayerDetail` continua
 * atribuível a este tipo, então os callers existentes seguem válidos.
 */
export type ApprovalLayerForGroup = Pick<ApprovalLayerDetail, 'order' | 'operator'> & {
  roles: { role: ValidationRole; weight: number }[]
}

export function apiLayersToGroup(layers: ApprovalLayerForGroup[]): ApprovalGroup {
  if (!layers.length) return createEmptyGroup()

  const sorted = [...layers].sort((a, b) => a.order - b.order)
  const rootLayer = sorted[0]

  const children: ApprovalGroupChild[] = rootLayer.roles
    .sort((a, b) => a.weight - b.weight)
    .map((r) => ({ type: 'rule' as const, value: r.role }))

  for (let i = 1; i < sorted.length; i++) {
    const nestedLayer = sorted[i]
    children.push({
      type: 'group' as const,
      operator: nestedLayer.operator ?? LayerOperator.AND,
      children: nestedLayer.roles
        .sort((a, b) => a.weight - b.weight)
        .map((r) => ({ type: 'rule' as const, value: r.role }))
    })
  }

  return {
    type: 'group',
    operator: rootLayer.operator ?? LayerOperator.AND,
    children
  }
}

export function groupToApiLayers(group: ApprovalGroup): CreateApprovalLayerRequest[] {
  const layers: CreateApprovalLayerRequest[] = []
  flattenGroupToLayers(group, layers)
  return layers.filter((l) => l.roles.length > 0)
}

export function hasValidRoles(group: ApprovalGroup): boolean {
  return collectRolesFromGroup(group).length > 0
}

export function allRulesHaveRoles(group: ApprovalGroup): boolean {
  for (const child of group.children) {
    if (child.type === 'rule' && child.value === null) return false
    if (child.type === 'group' && !allRulesHaveRoles(child)) return false
  }
  return true
}
